/**
 * PriceHistoryLine — how the area's prices have moved, over the window we
 * actually hold.
 *
 * The brief asked for ten years. WingCaster does not have ten years: the
 * snapshot producer backfills as far as the evidence reaches, which today is
 * months and grows two ways — forward as quarters close, and backward as
 * agents report older verified sales (a sale filed today may have closed two
 * years ago). An axis captioned with a decade over eighteen months of data
 * would be the same lie as a fabricated comparable, so the chart states its
 * real window in the line an agent reads before the line they look at.
 *
 * It widens on its own. In ten years it is a ten-year chart and will have
 * earned it.
 *
 * Medians here are SOLD prices only — the producer never averages a sold
 * price with an asking price, because they are different quantities
 * (`price-trend-producer.js`). A quarter the producer marked thin is drawn
 * hollow and dashed: the line still passes through it, since omitting it
 * would imply a gap in the market rather than a gap in what we know.
 *
 * Hand-rolled SVG (spec §9: no charting library, ≤ ~200 lines). RTL mirrors
 * the time axis so it reads oldest-right; numerals stay Western.
 */

import type { PricingTrendSnapshot } from '@/types/marketPricing'
import { formatMoney } from './pricingIntelligence'
import { priceBriefT, type PriceBriefLocale } from './copy'
import { rulerGeometry, rulerX, type RulerDirection } from './priceScale'

const WIDTH = 600
const HEIGHT = 180
const PAD_X = 46
const TOP = 18
const BOTTOM = 132

/** Below this many observations a quarter's median is a number, not a reading. */
export const THIN_QUARTER = 4

export interface HistoryPoint {
  key: string
  value: number
  count: number
  thin: boolean
}

/**
 * Snapshots to plottable points. Exported so the page and its tests can ask
 * "is there enough to draw?" without reaching into the SVG.
 */
export function historyPoints(snapshots: PricingTrendSnapshot[]): HistoryPoint[] {
  return [...(snapshots ?? [])]
    .filter((s) => s?.median_price != null && Number(s.median_price) > 0)
    .sort((a, b) => a.year - b.year || a.quarter - b.quarter)
    .map((s) => {
      const count = Number(s.properties_count) || 0
      return {
        key: `${s.year}-Q${s.quarter}`,
        value: Number(s.median_price),
        count,
        thin: s.confidence === 'low' || count < THIN_QUARTER,
      }
    })
}

interface PriceHistoryLineProps {
  snapshots: PricingTrendSnapshot[]
  currency: string
  dir: RulerDirection
  locale: PriceBriefLocale
}

export function PriceHistoryLine({ snapshots, currency, dir, locale }: PriceHistoryLineProps) {
  const t = (key: Parameters<typeof priceBriefT>[0], vars?: Record<string, string | number>) =>
    priceBriefT(key, locale, vars)

  const points = historyPoints(snapshots)

  // A trend needs two readings. One quarter is a fact, not a direction.
  if (points.length < 2) {
    return (
      <div
        data-testid="history-empty"
        className="rounded-[var(--lc-radius-lg)] border border-dashed border-[var(--lc-border-strong)] bg-[var(--lc-surface-sunken)] px-[var(--lc-space-md)] py-[var(--lc-space-sm)]"
      >
        <p className="m-0 text-[length:var(--lc-type-body)] text-[var(--lc-text-muted)]">
          {t('historyLabel')}
        </p>
        <p className="m-0 mt-[var(--lc-space-2xs)] text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
          {t('historyTooShort')}
        </p>
      </div>
    )
  }

  const values = points.map((p) => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const pad = (max - min || max * 0.1) * 0.18
  const lo = Math.max(0, min - pad)
  const hi = max + pad

  const geom = rulerGeometry({ min: 0, max: points.length - 1 }, WIDTH, PAD_X)
  const x = (index: number) => rulerX(geom, index, dir)
  const y = (value: number) => BOTTOM - ((value - lo) / (hi - lo || 1)) * (BOTTOM - TOP)

  const first = points[0]
  const last = points[points.length - 1]
  const changePercent = first.value > 0 ? ((last.value - first.value) / first.value) * 100 : 0
  const quarters = points.length
  const windowLabel =
    quarters >= 8
      ? t('historyWindowYears', { years: (quarters / 4).toFixed(quarters % 4 === 0 ? 0 : 1) })
      : t('historyWindowQuarters', { quarters })

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.value)}`).join(' ')
  // At most six labels, or the axis becomes a wall of numerals.
  const labelEvery = Math.max(1, Math.ceil(points.length / 6))
  const axisValues = [lo, (lo + hi) / 2, hi]

  return (
    <figure data-testid="price-history" className="m-0">
      <figcaption className="mb-[var(--lc-space-2xs)] text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-secondary)]">
        {t('historyChange', {
          change: `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(1)}`,
          window: windowLabel,
          from: first.key,
          to: last.key,
        })}
      </figcaption>

      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        data-dir={dir}
        aria-label={t('historyAria', {
          from: first.key,
          to: last.key,
          direction: changePercent >= 0 ? t('historyUp') : t('historyDown'),
          change: Math.abs(changePercent).toFixed(1),
        })}
        className="block w-full"
      >
        {axisValues.map((value) => (
          <g key={value}>
            <line
              x1={geom.x0}
              y1={y(value)}
              x2={geom.x1}
              y2={y(value)}
              stroke="var(--lc-border)"
              strokeOpacity="0.5"
              strokeDasharray="2 5"
            />
            <text
              x={dir === 'rtl' ? geom.x1 + 8 : geom.x0 - 8}
              y={y(value) + 3}
              textAnchor={dir === 'rtl' ? 'start' : 'end'}
              className="fill-[var(--lc-text-muted)]"
              style={{ font: 'var(--lc-type-data-sm)' }}
            >
              {formatMoney(value, currency)}
            </text>
          </g>
        ))}

        <path d={path} fill="none" stroke="var(--lc-accent-bold)" strokeWidth="2.25" strokeLinejoin="round" />

        {points.map((point, index) => (
          <g key={point.key}>
            <circle
              cx={x(index)}
              cy={y(point.value)}
              r={point.thin ? 4 : 3.5}
              fill={point.thin ? 'var(--lc-surface)' : 'var(--lc-accent-bold)'}
              stroke="var(--lc-accent-bold)"
              strokeWidth={point.thin ? 1.5 : 1}
              strokeDasharray={point.thin ? '2 2' : undefined}
              data-thin={point.thin ? 'true' : 'false'}
            >
              <title>
                {t('historyPointTitle', {
                  quarter: point.key,
                  price: formatMoney(point.value, currency),
                  count: point.count,
                })}
              </title>
            </circle>
            {index % labelEvery === 0 && (
              <text
                x={x(index)}
                y={BOTTOM + 18}
                textAnchor="middle"
                className="fill-[var(--lc-text-muted)]"
                style={{ font: 'var(--lc-type-caption)' }}
              >
                {point.key}
              </text>
            )}
          </g>
        ))}
      </svg>

      <p className="m-0 mt-[var(--lc-space-2xs)] text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
        {t('historyBasis')}
      </p>
    </figure>
  )
}
